import { Component, computed, inject } from '@angular/core';
import { MatSidenavModule } from '@angular/material/sidenav';
import { ProjectSelector } from '@shells/app/project-selector/project-selector';
import { MatExpansionModule } from '@angular/material/expansion';
import { TranslatePipe } from '@ngx-translate/core';
import { RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { PermissionsService } from '@core/permissions/permissions.service';
import { ResourcePermissionsEnum } from '@core/permissions/permissions.enum';
import { ResourcePermissionType } from '@core/permissions/models/resource-permission.model';

interface PagePermission {
  page: string;
  hasPermission: boolean;
}

type PageType = 'admin' | 'projectAdmin' | 'cuts' | 'reports';

@Component({
  selector: 'app-sidebar',
  imports: [
    MatExpansionModule,
    MatIconModule,
    MatSidenavModule,
    ProjectSelector,
    RouterLink,
    TranslatePipe,
  ],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.scss',
})
/**
 * The navigation is a pure derivation of the permissions of the active project,
 * so every section is computed: switching project re-runs these and the nav
 * re-renders on its own, with no reload and nothing to keep in sync.
 *
 * The project tracker link is the deliberate exception. It is the home route —
 * unguarded, and where a denial sends a user with nowhere to go back to — so it
 * is rendered unconditionally rather than derived from a permission.
 */
export class Sidebar {
  private permissionsService = inject(PermissionsService);

  private readonly adminPagePermissions = computed<PagePermission[]>(() => {
    const isSuperAdmin = this.permissionsService.isSuperAdmin();

    return [
      { page: 'menu', hasPermission: isSuperAdmin },
      { page: 'users', hasPermission: this.hasPermission(ResourcePermissionsEnum.Usuario) },
      { page: 'projects', hasPermission: this.hasPermission(ResourcePermissionsEnum.Project) },
      {
        page: 'apiKeys',
        hasPermission: this.hasPermission(ResourcePermissionsEnum.SecurityApiKeys),
      },
      { page: 'newBulkCut', hasPermission: isSuperAdmin },
      { page: 'viewBulkCut', hasPermission: isSuperAdmin },
      { page: 'roles', hasPermission: isSuperAdmin },
    ];
  });

  private readonly projectAdminPagePermissions = computed<PagePermission[]>(() => [
    { page: 'menu', hasPermission: this.hasPermission(ResourcePermissionsEnum.Project) },
    {
      page: 'crew',
      hasPermission: this.hasPermission(ResourcePermissionsEnum.EngineerCrewXProject),
    },
    { page: 'settings', hasPermission: this.hasPermission(ResourcePermissionsEnum.Project) },
    {
      page: 'activityLog',
      hasPermission: this.hasPermission(ResourcePermissionsEnum.ActivityLog),
    },
    { page: 'fileManager', hasPermission: this.hasPermission(ResourcePermissionsEnum.File) },
  ]);

  private readonly cutsPagePermissions = computed<PagePermission[]>(() => [
    {
      page: 'menu',
      hasPermission:
        this.hasPermission(ResourcePermissionsEnum.Cut) ||
        this.hasPermission(ResourcePermissionsEnum.Cut, 'canCreate'),
    },
    { page: 'newCut', hasPermission: this.hasPermission(ResourcePermissionsEnum.Cut, 'canCreate') },
    { page: 'viewCuts', hasPermission: this.hasPermission(ResourcePermissionsEnum.Cut) },
    { page: 'viewOnlineCuts', hasPermission: this.hasPermission(ResourcePermissionsEnum.Cut) },
  ]);

  private readonly reportsPagePermissions = computed<PagePermission[]>(() => [
    {
      page: 'menu',
      hasPermission:
        this.hasPermission(ResourcePermissionsEnum.ProjectTracker) ||
        this.hasPermission(ResourcePermissionsEnum.SprayerPerformance) ||
        this.hasPermission(ResourcePermissionsEnum.MainBoard),
    },
    { page: 'mainDashboard', hasPermission: this.hasPermission(ResourcePermissionsEnum.MainBoard) },
    {
      page: 'applicationOverview',
      hasPermission: this.hasPermission(ResourcePermissionsEnum.ApplicationOverview),
    },
    {
      page: 'progressOverview',
      hasPermission: this.hasPermission(ResourcePermissionsEnum.ProgressOverview),
    },
    {
      page: 'shotcreteOverview',
      hasPermission: this.hasPermission(ResourcePermissionsEnum.ShotcreteOverview),
    },
    {
      page: 'sprayerPerformance',
      hasPermission: this.hasPermission(ResourcePermissionsEnum.SprayerPerformance),
    },
    { page: 'cutSummary', hasPermission: this.hasPermission(ResourcePermissionsEnum.CutCounter) },
  ]);

  findPermission(pageType: PageType, page: string): boolean {
    let permission;

    switch (pageType) {
      case 'admin':
        permission = this.adminPagePermissions().find((p) => p.page === page);
        break;
      case 'projectAdmin':
        permission = this.projectAdminPagePermissions().find((p) => p.page === page);
        break;
      case 'cuts':
        permission = this.cutsPagePermissions().find((p) => p.page === page);
        break;
      case 'reports':
        permission = this.reportsPagePermissions().find((p) => p.page === page);
        break;
      default:
        permission = undefined;
    }
    return permission ? permission.hasPermission : false;
  }

  private hasPermission(
    resource: ResourcePermissionsEnum,
    type: ResourcePermissionType = 'menu',
  ): boolean {
    return this.permissionsService.hasResourcePermission(resource, type);
  }
}
